/**
 * Panel principal: resumen del día, saldos y estado de sincronización.
 */

import { useEffect, useState } from 'react';
import { formatInstant, formatMoney, fromRow, type SyncSummary } from '@senm/domain';
import { db } from '../local/db.js';
import type { StoredSession } from '../local/session.js';
import type { Route } from '../App.js';

interface Resumen {
  estudiantes: number;
  conSaldo: number;
  saldoTotalMinor: number;
  cobradoHoyMinor: number;
  pagosHoy: number;
  moneda: string;
}

export function DashboardPage(props: {
  session: StoredSession;
  resumen: SyncSummary | null;
  ultimaSync: string | null;
  onIr: (ruta: Route) => void;
}): JSX.Element {
  const [datos, setDatos] = useState<Resumen | null>(null);

  useEffect(() => {
    void (async () => {
      const estudiantes = await db.students.toArray();
      const pagos = await db.payments.toArray();
      const hoy = new Date().toISOString().slice(0, 10);
      const pagosHoy = pagos.filter(
        (pago) => pago.operationalDate === hoy && pago.status !== 'RECHAZADO' && pago.status !== 'EN_CONFLICTO',
      );

      setDatos({
        estudiantes: estudiantes.length,
        conSaldo: estudiantes.filter((estudiante) => estudiante.outstandingMinor > 0).length,
        saldoTotalMinor: estudiantes.reduce((total, estudiante) => total + estudiante.outstandingMinor, 0),
        cobradoHoyMinor: pagosHoy.reduce((total, pago) => total + pago.amountMinor, 0),
        pagosHoy: pagosHoy.length,
        moneda: estudiantes[0]?.currency ?? props.session.user.currency,
      });
    })();
  }, [props.session.user.currency]);

  const pendientes = props.resumen?.pending ?? 0;
  const revision = props.resumen?.conflicted ?? 0;

  return (
    <>
      <h1>Panel</h1>
      <p className="ayuda">
        {props.session.user.institutionName} — {props.session.user.fullName}
      </p>

      {props.ultimaSync === null ? (
        <div className="aviso-caja" role="status">
          Este dispositivo aún no ha descargado datos. Conéctese y sincronice.
        </div>
      ) : (
        <p className="ayuda">
          Datos locales al {formatInstant(props.ultimaSync, props.session.user.timeZone)}. Los saldos
          mostrados provienen de esa sincronización.
        </p>
      )}

      <div className="rejilla">
        <div className="tarjeta dato">
          <span className="etiqueta">Cobrado hoy ({datos?.pagosHoy ?? 0} pagos)</span>
          <span className="valor">
            {datos === null ? '—' : formatMoney(fromRow(datos.cobradoHoyMinor, datos.moneda))}
          </span>
        </div>
        <div className="tarjeta dato">
          <span className="etiqueta">Saldo pendiente total</span>
          <span className="valor">
            {datos === null ? '—' : formatMoney(fromRow(datos.saldoTotalMinor, datos.moneda))}
          </span>
        </div>
        <div className="tarjeta dato">
          <span className="etiqueta">Estudiantes con saldo</span>
          <span className="valor">
            {datos === null ? '—' : `${datos.conSaldo} de ${datos.estudiantes}`}
          </span>
        </div>
        <div className="tarjeta dato">
          <span className="etiqueta">Operaciones sin enviar</span>
          <span className="valor">{pendientes}</span>
        </div>
      </div>

      {revision > 0 && (
        <div className="aviso-caja" role="status">
          Hay {revision} operación(es) en revisión.{' '}
          <button type="button" className="boton" onClick={() => props.onIr('sincronizacion')}>
            Ver detalle
          </button>
        </div>
      )}

      <h2>Acciones</h2>
      <div className="pila">
        <button type="button" className="boton primario" onClick={() => props.onIr('estudiantes')}>
          Buscar estudiante y registrar pago
        </button>
        <button type="button" className="boton" onClick={() => props.onIr('sincronizacion')}>
          Centro de sincronización
        </button>
        <button type="button" className="boton" onClick={() => props.onIr('reportes')}>
          Reportes
        </button>
      </div>
    </>
  );
}
